import {
  useGetTrackDetailCountryApiV1Suspense,
  useGetTrackDetailOverviewApiV1Suspense,
  useGetTrackDetailPlatformApiV1Suspense,
} from "@/api/__generated__/api.schema";
import { useGetTrackDetailApiV1Suspense } from "@/api/__generated__/api.schema";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { cn } from "@/lib/utils";
import { CountryFlag } from "@/routes/custom-music/dsp-analytics/components/graphics/country-flag";
import { PlatformFlag } from "@/routes/custom-music/dsp-analytics/components/graphics/platform-flag";
import { RevenueBreakdownTable } from "@/routes/custom-music/dsp-analytics/components/graphics/revenue-breakdown-table";
import { MonthRangeSelect } from "@/routes/custom-music/dsp-analytics/components/month-range-select";
import { useAnalyticsMonth } from "@/routes/custom-music/dsp-analytics/context/dsp-analytics-context";
import { formatNumber } from "@/utils/format";
import { formatCurrency } from "@/utils/format";
import { formatMonthString } from "@/utils/time";
import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  Image,
  InteractiveTable,
  Loading,
  ShadowBox,
  TableBody,
  TableCell,
  TableHead,
  TableHeadRow,
  TableHeader,
  TableRow,
  Typography,
} from "@backpac/monica";
import { Suspense } from "react";

interface TrackDetailProps {
  code: string;
}

export const TrackDetail = (props: TrackDetailProps) => {
  return (
    <Suspense fallback={<TrackDetail.Pending />}>
      <TrackDetail.Contents {...props} />
    </Suspense>
  );
};

TrackDetail.Pending = () => {
  return (
    <div className="p-8">
      <div className="flex flex-row justify-between">
        <Typography font="unbounded" size="xl" color="black">
          Track Details
        </Typography>
        <MonthRangeSelect />
      </div>
      <div className="flex flex-1 items-center justify-center mt-8 bg-white rounded-lg h-[236px]">
        <Loading />
      </div>
      <div className="flex flex-col justify-between mt-6 rounded-lg gap-6">
        <TrackDetail.RevenueCollapsible title="Revenue">
          <div />
        </TrackDetail.RevenueCollapsible>
        <TrackDetail.RevenueCollapsible title="Platform">
          <div />
        </TrackDetail.RevenueCollapsible>
        <TrackDetail.RevenueCollapsible title="Country">
          <div />
        </TrackDetail.RevenueCollapsible>
      </div>
    </div>
  );
};

TrackDetail.Contents = ({ code }: TrackDetailProps) => {
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { item: track },
    },
  } = useGetTrackDetailApiV1Suspense(workspaceId!, code);

  const details = [
    {
      label: "Album title",
      value: track.album,
    },
    {
      label: "Artist",
      value: track.artist,
    },
    {
      label: "Released date",
      value: track.releaseDate,
    },
    {
      label: "ISRC",
      value: track.isrc,
    },
    {
      label: "UPC",
      value: track.upc,
    },
  ];

  return (
    <div className="p-8">
      <div className="flex flex-row justify-between">
        <Typography font="unbounded" size="xl" color="black">
          Track Details
        </Typography>
        <MonthRangeSelect />
      </div>
      <ShadowBox className="flex flex-col justify-between mt-8 p-6 gap-5">
        <div className="flex flex-row gap-2">
          <Typography font="unbounded" size="md" color="black">
            {track.title}
          </Typography>
          <div>
            <Badge theme="green">Active</Badge>
          </div>
        </div>

        <div className="flex flex-row gap-8">
          <Image src={track.coverImageUrl} alt="track-detail" width={144} height={144} />
          <div className="flex flex-1 flex-col flex-wrap gap-2">
            {details.map((detail) => (
              <div className="flex flex-row justify-between w-[240px]">
                <Typography size="xs" color="textMuted">
                  {detail.label}
                </Typography>
                <Typography size="xs" color="textPrimary" weight={600}>
                  {detail.value}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </ShadowBox>
      <div className="flex flex-col justify-between mt-6 rounded-lg gap-6">
        <TrackDetail.Revenue code={code} />
        <TrackDetail.Platform code={code} />
        <TrackDetail.Country code={code} />
      </div>
    </div>
  );
};

TrackDetail.Revenue = ({ code }: { code: string }) => {
  const workspaceId = useWorkspaceId();
  const { selectedMonthRange } = useAnalyticsMonth();
  const {
    data: {
      data: { item: analytics },
    },
  } = useGetTrackDetailOverviewApiV1Suspense(workspaceId, code, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });

  return (
    <TrackDetail.RevenueCollapsible title="Revenue" defaultOpen={true} amount={analytics.revenue}>
      <InteractiveTable className="shadow-none pb-0">
        <TableHeader>
          <TableHeadRow>
            <TableHead>Settlement</TableHead>
            <TableHead>Revenue</TableHead>
            <TableHead>Stream</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {analytics.monthlyData
            .filter((md) => md.salesData.length > 0)
            .map((item) => (
              <>
                <TableRow key={item.settlementMonth} className="border-none">
                  <TableCell>
                    <Typography size="xs" color="textPrimary" weight={600}>
                      {item.settlementMonth}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography size="xs" color="textPrimary" weight={600}>
                      {formatCurrency(item.revenue)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography size="xs" color="textPrimary" weight={600}>
                      {formatNumber(item.streams, 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
                {item.salesData.map((sale, idx) => (
                  <TableRow
                    key={sale.saleMonth}
                    className={idx === item.salesData.length - 1 ? "border-b" : "border-none"}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        ⤷
                        <Typography size="xs" color="textMuted">
                          {sale.saleMonth} Sales
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <Typography size="xs" color="textMuted">
                        {formatCurrency(sale.revenue)}
                      </Typography>
                    </TableCell>
                    <TableCell className="py-2">
                      <Typography size="xs" color="textMuted">
                        {formatNumber(sale.streams, 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ))}
        </TableBody>
      </InteractiveTable>
    </TrackDetail.RevenueCollapsible>
  );
};

TrackDetail.Platform = ({ code }: { code: string }) => {
  const workspaceId = useWorkspaceId();
  const { selectedMonthRange } = useAnalyticsMonth();
  const {
    data: {
      data: { items: analytics },
    },
  } = useGetTrackDetailPlatformApiV1Suspense(workspaceId, code, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });

  return (
    <RevenueBreakdownTable
      title="Platform"
      analytics={analytics}
      additionalColumns={[
        {
          label: "Platform",
          render: (item) => <PlatformFlag platform={item.name} />,
          position: "start",
        },
      ]}
    />
  );
};

TrackDetail.Country = ({ code }: { code: string }) => {
  const workspaceId = useWorkspaceId();
  const { selectedMonthRange } = useAnalyticsMonth();
  const {
    data: {
      data: { items: analytics },
    },
  } = useGetTrackDetailCountryApiV1Suspense(workspaceId, code, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });

  return (
    <RevenueBreakdownTable
      title="Country"
      analytics={analytics}
      additionalColumns={[
        {
          label: "Country",
          render: (item) => <CountryFlag countryCode={item.name} />,
          position: "start",
        },
      ]}
    />
  );
};

interface Props {
  title: string;
  amount?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

TrackDetail.RevenueCollapsible = ({ defaultOpen, title, amount, children }: Props) => {
  const { formatedMonthRange } = useAnalyticsMonth();
  return (
    <ShadowBox className="overflow-hidden">
      <Collapsible defaultOpen={defaultOpen} className="bg-white p-6">
        <CollapsibleTrigger className="w-full">
          {(open) => (
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-col items-start  gap-2">
                <Typography size="md" font="unbounded" color="textPrimary">
                  {title}
                </Typography>
                {amount == null ? (
                  <Typography size="xs" color="textMuted">
                    {formatedMonthRange}
                  </Typography>
                ) : (
                  <Typography size="lg" color="textPrimary" weight={600}>
                    {formatCurrency(amount)}
                  </Typography>
                )}
              </div>
              <Icon
                name="chevron-down"
                iconSize="16px"
                className={cn("transition-transform duration-200", open && "rotate-180")}
              />
            </div>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>{children}</CollapsibleContent>
      </Collapsible>
    </ShadowBox>
  );
};
